import { useState } from "react";
import { submitContactForm } from '../api/contactApi';

const DEFAULT_IMG =
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1600&auto=format&fit=crop"; // replace with your image
  // https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1600&auto=format&fit=crop
  // "https://images.unsplash.com/photo-1520975916090-3105956dac38?q=80&w=1600&auto=format&fit=crop";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, success: false, err: "" });

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ loading: true, success: false, err: "" });
    console.log('[contact-page] send clicked', {
      name: form.name,
      email: form.email,
      subject: form.subject,
      hasPhone: Boolean(form.phone?.trim()),
      hasMessage: Boolean(form.message?.trim()),
    });

    // minimal client validation
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ loading: false, success: false, err: "Name, Email, and Message are required." });
      return;
    }

    try {
      await submitContactForm(form);
      console.log('[contact-page] submit success');
      setStatus({ loading: false, success: true, err: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error('[contact-page] submit failure', err);
      setStatus({ loading: false, success: false, err: String(err.message || err) });
    }
  }

  return (
    <section className="mx-auto max-w-7xl px-6 py-10">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 items-stretch">
        {/* Left: image */}
        <div className="relative rounded-2xl overflow-hidden min-h-[320px] lg:min-h-[560px]">
          <img
            src={DEFAULT_IMG}
            alt="Team collaborating around a table on a technology project"
            width="800"
            height="560"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/30 to-transparent" />
          <div className="relative z-10 p-6 sm:p-10 text-white bg-gradient-to-tr from-blue-400/30 to-gray-800/30 flex flex-col justify-center h-full">
            <h2 className="text-3xl font-bold">Let’s build something great</h2>
            <p className="mt-3 max-w-md text-white/90">
              Tell me about your project, timeline, and goals. I’ll get back within 1–2 business days.
            </p>
          </div>
        </div>

        {/* Right: form */}
        <div className="rounded-2xl border p-6 sm:p-8">
          <h1 className="text-2xl font-semibold">Contact Angelo Ruiz for IT Consulting</h1>
          <p className="mt-1 text-gray-600">I usually respond within a day.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  id="name" name="name" value={form.name} onChange={onChange}
                  autoComplete="name" required
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email *
                </label>
                <input
                  id="email" name="email" type="email" value={form.email} onChange={onChange}
                  autoComplete="email" required
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <input
                  id="phone" name="phone" value={form.phone} onChange={onChange}
                  autoComplete="tel"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  id="subject" name="subject" value={form.subject} onChange={onChange}
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                Message *
              </label>
              <textarea
                id="message" name="message" required rows={6}
                value={form.message} onChange={onChange}
                className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Briefly describe your project, timeline, and budget (if you have one)."
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={status.loading}
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
              >
                {status.loading ? "Sending…" : "Send message"}
              </button>

              {status.success && <span className="text-sm text-green-600">Thanks! I’ll reply soon.</span>}
              {status.err && <span className="text-sm text-red-600">{status.err}</span>}
            </div>
          </form>

          {/* Direct email fallback */}
          <p className="mt-6 text-sm text-gray-500">
            Prefer email? <a href="mailto:info@angeloruiz.com" className="text-indigo-600 hover:underline">info@angeloruiz.com</a>
          </p>
          <p className="mt-3 text-sm text-gray-600">You can also connect on <a href="https://www.linkedin.com/in/arcode/" className="text-indigo-600 underline">LinkedIn</a>. For consulting, implementation, cloud administration or software inquiries, describe the current workflow and what you would like to improve.</p>
          <p className="mt-3 text-sm text-gray-500">Please avoid sending passwords or confidential client records. See the <a href="/privacy-policy" className="text-indigo-600 underline">Privacy Policy</a> for how inquiries are handled.</p>
        </div>
      </div>
    </section>
  );
}
